import { Component, contentChildren, DestroyRef, effect, ElementRef, inject, input, model, output, Renderer2 } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthApiService } from '@api/service/auth.api.service';
import { UserService } from '@core/client/user.service';
import { ErrorService } from '@core/common/error.service';
import { CustomValidators, FormDisplayControl } from '@oi-soft/common';
import { VoltaCustomValidators } from '@shared/classes/volta-custom-validators';
import { ModalComponent } from '@shared/components/modals/modal/modal.component';
import { VoltaSweetAlertService } from '@shared/services/volta-sweet-alert/volta-sweet-alert.service';
import { firstValueFrom } from 'rxjs';
import { FormControlComponent } from "../../../../shared/components/form/form-control/form-control.component";
import { ModalFormButtonGroupComponent } from "../../../../shared/components/modals/modal/modal-button-group/modal-form-button-group/modal-form-button-group.component";

@Component({
    selector: 'app-change-password-modal',
    imports: [ModalComponent, FormControlComponent, ReactiveFormsModule, ModalFormButtonGroupComponent],
    templateUrl: './change-password-modal.component.html',
    styleUrl: './change-password-modal.component.scss'
})
export class ChangePasswordModalComponent {
    
    private swalService = inject(VoltaSweetAlertService);
    private authApiService = inject(AuthApiService);
    private errorService = inject(ErrorService);
    private userService = inject(UserService);
    private destroyRef = inject(DestroyRef);
    private renderer = inject(Renderer2);
    
    private modalShow = contentChildren('modalShow', { read: ElementRef });

    close = input<boolean>(false);
    signout = input<boolean>(true);
    
    visible = model<boolean>(false);

    passwordChanged = output<boolean>({alias: 'password-changed'});
    
    form = new FormGroup({
        oldPassword: new FormDisplayControl(
            'Old Password', '',
            {
                validators: [Validators.required]
            }
        ),
        newPassword: new FormDisplayControl(
            'New Password', '',
            {
                validators: [Validators.required, VoltaCustomValidators.userPassword]
            }
        ),
        confirmPassword: new FormDisplayControl(
            'Confirm Password', '',
            {
                validators: [Validators.required, CustomValidators.equalToControl("newPassword")]
            }
        )
    });

    constructor(){
        effect(() => {
            if(!this.visible()){
                this.form.reset();
            }
        })
    }
    
    ngAfterViewInit(): void {
        
        //Add open functionality to "modalShow" if it is present
        this.modalShow()?.forEach((refShow)=>{
            const clickUnlisten = this.renderer.listen(refShow.nativeElement, 'click', () => {
                this.show();
            });

            this.destroyRef.onDestroy(() => {
                clickUnlisten();
            });
        });
    }
    
    show(){
        this.visible.set(true);
    }
    
    hide(){
        this.visible.set(false);
    }
    
    onSubmit(){
        if(this.form.valid){
            
            this.swalService.fireLoading({message: "Updating user password, please wait."});
            
            const subscription = this.authApiService.Auth.Manage.Password.Post({
                body: {
                    oldPassword: this.form.value.oldPassword,
                    newPassword: this.form.value.confirmPassword
                }
            })
            .subscribe({
                next: () => {
                    this.swalService.fireSuccess({title: "Password changed", message: "Please re-login.", timer: true})
                    this.errorService.onUserUnauthorised();
                    this.userService.cleanUser();
                    this.hide();
                    this.passwordChanged.emit(true);
                },
                error: (error) => {
                    this.swalService.fireError(error);
                }
            })
            
            this.destroyRef.onDestroy(() => {
                subscription.unsubscribe();
            });
            
        }
        else{
            
            Object.keys(this.form.controls).forEach(key => {
                this.form.get(key)!.markAsDirty();
                this.form.get(key)!.markAsTouched();
            });
            
        }
    }

    onSignOut(){
        this.swalService.fireLoading({message: "Logging you out, please wait."});

        firstValueFrom(this.userService.userLogOut())
        .then((response) => {
            this.swalService.close();
            this.hide();
        })
    }
    
}
